import React, { useEffect, useState, useMemo } from "react";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isItemOutOfStock } from "../../helpers/utils";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { useIntl } from "react-intl";
import { faTag } from "@fortawesome/free-solid-svg-icons";
import { formatDecimal } from "../../helpers/itemCalculations";
import { translatedName } from "../../helpers/translations";
import { usePromotionsContext } from "../../hooks/usePromotionsContext";
import { modifierBreakdown } from "./modifierBreakdown";

interface ICartIndividualItem {
  cartItem: any;
  cartType?: string;
  incrementCount(item: any): void;
  decrementCount(item: any): void;
  removeItem(item: any): void;
  showCartModifyError(): void;
}

const CartItem = (props: ICartIndividualItem) => {
  const [itemOutOfStock, setItemOutOfStock] = useState(false);
  let { currency } = useSelector((state: RootState) => state.company);
  let { out_of_stock_items } = useSelector((state: RootState) => state.menu);
  const { appliedPromotionsData } = usePromotionsContext();

  const intl = useIntl();

  useEffect(() => {
    if (out_of_stock_items.length) {
      if (isItemOutOfStock(props.cartItem, out_of_stock_items)) {
        setItemOutOfStock(true);
      }
    }
  }, [props.cartItem, out_of_stock_items]);

  const removeCartItem = () => {
    props.removeItem(props.cartItem);
  };

  const updateCartItem = (operation: string) => {
    if (props.cartType === "dineInCart") {
      props.showCartModifyError();
    } else if (itemOutOfStock) {
      return;
    } else {
      if (operation === "increment") {
        props.incrementCount(props.cartItem);
      } else {
        props.cartItem.count > 1
          ? props.decrementCount(props.cartItem)
          : removeCartItem();
      }
    }
  };

  const appliedPromotionName = useMemo(() => {
    let promotionName = "";
    let appliedPromotionId = props.cartItem.applied_promotion_id || null;
    if (appliedPromotionId) {
      const appliedPromotion = appliedPromotionsData.appliedPromotions.find(
        (promotion) => promotion.id === appliedPromotionId
      );
      promotionName = appliedPromotion?.name || "";
    }
    return promotionName;
  }, [props.cartItem, appliedPromotionsData.appliedPromotions]);

  const itemBreakdown = useMemo(() => {
    const cartItem = props.cartItem;
    let itemTotalBeforePromotion = 0;
    let itemTotalAfterPromotion = 0;
    let isPromotionApplied = false;
    if (cartItem.applied_promotion_id && cartItem.itemTotalBeforePromotion) {
      // if promotion is applied, then itemTotalBeforePromotion is the price before promotion
      isPromotionApplied = true;
      itemTotalBeforePromotion =
        cartItem.itemTotalBeforePromotion * cartItem.count;
      itemTotalAfterPromotion = cartItem.sub_total;
    } else {
      // if no promotion is applied, then itemTotalBeforePromotion is the price before promotion
      isPromotionApplied = false;
      itemTotalBeforePromotion = cartItem.sub_total;
      itemTotalAfterPromotion = cartItem.sub_total;
    }
    return {
      itemTotalBeforePromotion,
      itemTotalAfterPromotion,
      isPromotionApplied,
    };
  }, [props.cartItem]);

  return (
    <>
      <div
        className={`order-item ${
          props.cartItem.groups.length ? "with-group" : ""
        } ${props.cartType === "dineInCart" ? "grey-out" : ""}`}
      >
        <div
          className={`delete-icon ${props.cartItem.is_free_item ? "hide" : ""}`}
          onClick={() => {
            if (props.cartType === "dineInCart") {
              props.showCartModifyError();
            } else if (props.cartItem.is_free_item) {
              return;
            } else {
              props.removeItem(props.cartItem);
            }
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className={`item-details ${itemOutOfStock ? "grey-out" : ""}`}>
          <p className="item-name">
            {translatedName(props.cartItem, intl.locale)}
          </p>
          <div className="item-price d-flex flex-column">
            <div className="item-price-promotion">
              <span>
                {currency}{" "}
                {formatDecimal(itemBreakdown.itemTotalAfterPromotion)}
              </span>
            </div>
            {itemBreakdown.isPromotionApplied ? (
              <span
                className={`${
                  itemBreakdown.isPromotionApplied ? "line-through" : ""
                }`}
              >
                {currency}{" "}
                {formatDecimal(itemBreakdown.itemTotalBeforePromotion)}
              </span>
            ) : null}
          </div>
          {appliedPromotionName !== "" || props.cartItem.promotion_id ? (
            <p className="item-promotion-name">
              <span>
                <FontAwesomeIcon icon={faTag} />{" "}
                {appliedPromotionName || props.cartItem.promotion_name}
              </span>
            </p>
          ) : (
            false
          )}
        </div>
        {!props.cartItem.is_free_item ? (
          <div
            className={`count-controller ${itemOutOfStock ? "grey-out" : ""}`}
          >
            <p
              className="icon"
              onClick={() => {
                updateCartItem("decrement");
              }}
            >
              -
            </p>
            <p className="count">{props.cartItem.count}</p>
            <p
              className="icon"
              onClick={() => {
                updateCartItem("increment");
              }}
            >
              +
            </p>
          </div>
        ) : null}
      </div>
      {props.cartItem.groups.length ? (
        <div
          className={`selected-modifiers ${
            itemOutOfStock || props.cartType === "dineInCart" ? "grey-out" : ""
          } ${props.cartItem.is_free_item ? "free-modifier" : ""}`}
        >
          {modifierBreakdown(props.cartItem, false)}{" "}
          {/* passing false since div is greyed out */}
        </div>
      ) : (
        false
      )}
    </>
  );
};

export default CartItem;
